export interface CustomError {
  status?: number
  message: string
  data: string
}
type ShowSnackbarType = (
  message: string | null,
  severity: "default" | "error" | "success" | "warning" | "info" | undefined
) => void

const statusHandlers: {
  [key: number]: (showSnackbar: ShowSnackbarType, message?: string) => void
} = {
  401: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(message || "Unauthorized. Redirecting to login.", "error")
  },
  524: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(message || "Request Timed Out.", "error")
  },
  404: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(message || "A server could not find a client-requested API.", "error")
  },
  500: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(
      message ||
        "A server encountered an unexpected condition that prevented it from fulfilling the request.",
      "error"
    )
  },
}

/** Pulls a human-readable message out of whatever the API put in the error body. */
export const extractServerMessage = (data: unknown): string | undefined => {
  if (typeof data === "string") {
    return data.trim() || undefined
  }
  if (!data || typeof data !== "object") {
    return undefined
  }
  const record = data as Record<string, unknown>
  if (typeof record.message === "string" && record.message.trim()) {
    return record.message.trim()
  }
  // ASP.NET model-state: { "fieldName": ["message", ...], ... }
  const messages = Object.values(record)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === "string" && value.trim() !== "")
  return messages.length ? messages.join(" ") : undefined
}

export const asyncErrorHandler = <T extends unknown[], R>(
  asyncFunc: (...args: T) => Promise<R>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  showSnackbar: ShowSnackbarType
) => {
  return async (...params: T): Promise<R | void> => {
    try {
      return await asyncFunc(...params)
    } catch (error) {
      const typedError = error as CustomError

      // The API reports failures in three shapes, all surfaced by RTK Query as
      // `data`: HTTP 500 text/plain ("Insufficient balance"), HTTP 500 JSON
      // `{ error, message }` (RedeemConfirmationStart), and HTTP 400 JSON
      // model-state dictionaries `{ field: ["..."] }` when the Kiosk binder
      // rejects an unknown field. Prefer any of them over the generic text.
      const serverMessage = typedError.message || extractServerMessage(typedError.data)

      const handler = typedError.status && statusHandlers[typedError.status]
      if (handler) {
        handler(showSnackbar, serverMessage)
      } else {
        showSnackbar(serverMessage || "Something went wrong!", "error")
      }
    }
  }
}
